/**
 * QuizPage Component
 * Main quiz page with state machine for quiz flow
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Layout } from '../components/layout/Layout';
import { QuizStartScreen } from '../components/quiz/QuizStartScreen';
import { QuizQuestion } from '../components/quiz/QuizQuestion';
import { QuizResults } from '../components/quiz/QuizResults';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Button } from '../components/ui/Button';
import { quizService, type QuizSession, type QuizQuestion as QuizQuestionType, type QuizResults as QuizResultsType } from '../services/quiz.service';
import { useToast } from '../hooks/useToast';

type QuizState = 'start' | 'loading' | 'question' | 'results' | 'error';

export const QuizPage = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [quizState, setQuizState] = useState<QuizState>('start');
  const [session, setSession] = useState<QuizSession | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [correctOption, setCorrectOption] = useState<number | null>(null);
  const [showNextButton, setShowNextButton] = useState(false);
  const [results, setResults] = useState<QuizResultsType | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [autoAdvanceTimeout, setAutoAdvanceTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);

  // Start quiz mutation
  const startQuizMutation = useMutation({
    mutationFn: (quizType: 'random' | 'new' | 'all') => quizService.startQuiz(quizType),
    onSuccess: (data) => {
      setSession(data);
      setCurrentQuestionIndex(0);
      setQuizState('question');
    },
    onError: (error: any) => {
      setErrorMessage(error.response?.data?.message || 'Not enough members with profile pictures to create a quiz.');
      setQuizState('error');
    },
  });

  // Submit answer mutation
  const submitAnswerMutation = useMutation({
    mutationFn: ({ sessionId, questionId, selectedOption }: { sessionId: number; questionId: number; selectedOption: number }) =>
      quizService.submitAnswer(sessionId, questionId, selectedOption),
    onSuccess: (data) => {
      setIsCorrect(data.is_correct);
      setCorrectOption(data.correct_option);
      setShowNextButton(true);

      // Auto-advance after 3 seconds
      const timeout = setTimeout(() => {
        handleNextQuestion();
      }, 3000);
      setAutoAdvanceTimeout(timeout);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to submit answer');
    },
  });

  // Get results mutation
  const getResultsMutation = useMutation({
    mutationFn: (sessionId: number) => quizService.getResults(sessionId),
    onSuccess: (data) => {
      setResults(data);
      setQuizState('results');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to load results');
    },
  });

  // Cleanup timeout on unmount or question change
  useEffect(() => {
    return () => {
      if (autoAdvanceTimeout) {
        clearTimeout(autoAdvanceTimeout);
      }
    };
  }, [autoAdvanceTimeout]);

  // Keyboard shortcuts
  useEffect(() => {
    if (quizState !== 'question') return;

    const handleKeyPress = (e: KeyboardEvent) => {
      const currentQuestion = getCurrentQuestion();
      if (!currentQuestion) return;

      // Number keys 1-4 for answer selection
      if (e.key >= '1' && e.key <= '4' && selectedAnswer === null) {
        const optionIndex = parseInt(e.key) - 1;
        if (currentQuestion.options[optionIndex]) {
          handleAnswerSelected(currentQuestion.options[optionIndex].option_id);
        }
      }

      // Space or Enter for next question
      if ((e.key === ' ' || e.key === 'Enter') && showNextButton) {
        e.preventDefault();
        handleNextQuestion();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [quizState, selectedAnswer, showNextButton, currentQuestionIndex]);

  const handleStartQuiz = (quizType: 'random' | 'new' | 'all') => {
    setQuizState('loading');
    startQuizMutation.mutate(quizType);
  };

  const getCurrentQuestion = (): QuizQuestionType | null => {
    if (!session) return null;
    return session.questions[currentQuestionIndex] || null;
  };

  const handleAnswerSelected = (optionId: number) => {
    if (selectedAnswer !== null || !session) return;

    const currentQuestion = getCurrentQuestion();
    if (!currentQuestion) return;

    setSelectedAnswer(optionId);
    submitAnswerMutation.mutate({
      sessionId: session.session_id,
      questionId: currentQuestion.id,
      selectedOption: optionId,
    });
  };

  const handleNextQuestion = () => {
    if (!session) return;

    // Clear any existing timeout
    if (autoAdvanceTimeout) {
      clearTimeout(autoAdvanceTimeout);
      setAutoAdvanceTimeout(null);
    }

    const nextIndex = currentQuestionIndex + 1;

    if (nextIndex < session.questions.length) {
      // Move to next question
      setCurrentQuestionIndex(nextIndex);
      setSelectedAnswer(null);
      setIsCorrect(null);
      setCorrectOption(null);
      setShowNextButton(false);
    } else {
      // Quiz complete - get results
      setQuizState('loading');
      getResultsMutation.mutate(session.session_id);
    }
  };

  const handlePlayAgain = () => {
    // Reset all state
    setSession(null);
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setIsCorrect(null);
    setCorrectOption(null);
    setShowNextButton(false);
    setResults(null);
    setQuizState('start');
  };

  const handleBackToHome = () => {
    navigate('/home');
  };

  const handleRetry = () => {
    setQuizState('start');
    setErrorMessage('');
  };

  const currentQuestion = getCurrentQuestion();

  return (
    <Layout>
      <div className="space-y-6">
        {/* Quiz Content */}
        <div className="max-w-4xl mx-auto">
          {/* Start Screen */}
          {quizState === 'start' && <QuizStartScreen onStartQuiz={handleStartQuiz} />}

          {/* Loading Screen */}
          {quizState === 'loading' && (
            <div className="text-center py-12">
              <LoadingSpinner size="lg" text="Loading quiz questions..." />
            </div>
          )}

          {/* Question Screen */}
          {quizState === 'question' && currentQuestion && session && (
            <QuizQuestion
              question={currentQuestion}
              currentQuestionNumber={currentQuestionIndex + 1}
              totalQuestions={session.total_questions}
              onAnswerSelected={handleAnswerSelected}
              onNextQuestion={handleNextQuestion}
              selectedAnswer={selectedAnswer}
              isCorrect={isCorrect}
              correctOption={correctOption}
              showNextButton={showNextButton}
            />
          )}

          {/* Results Screen */}
          {quizState === 'results' && results && (
            <QuizResults
              results={results}
              onPlayAgain={handlePlayAgain}
              onBackToHome={handleBackToHome}
            />
          )}

          {/* Error Screen */}
          {quizState === 'error' && (
            <div className="text-center py-12 bg-surface rounded-lg shadow-lg p-8">
              <svg
                className="mx-auto h-12 w-12 text-yellow-500 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <h3 className="text-lg font-medium text-heading mb-2">Unable to Start Quiz</h3>
              <p className="text-meta mb-4">{errorMessage}</p>
              <Button onClick={handleRetry}>
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Try Again
              </Button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};
